/**
 * `ScenePresetSwitcher` — 3-pill preset switcher for the `/t/:gtwinId`
 * workspace header (WP-PP5 / RFC-108.1).
 *
 * Accessibility invariants (binding; do NOT skip):
 *   - All pills are keyboard reachable (tabIndex=0).
 *   - Enter / Space activates the focused pill.
 *   - Active pill carries `aria-pressed="true"`; inactive pills carry
 *     `aria-pressed="false"`.
 *   - A live region (`role="status"`) announces "Scene preset: <label>"
 *     on every switch.
 *
 * Token-only styling — zero raw hex colors.
 */

import { useRef, useState, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { useViewerStore } from '@/state/useViewerStore';
import { gtwinClient } from './TwinRoute';
import type { ScenePreset } from '@/sdk-stub/gtwin-sdk';
import { DEFAULT_PRESETS } from '@/sdk-stub/gtwin-sdk';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export interface ScenePresetSwitcherProps {
  gtwinId: string;
  presets?: ScenePreset[];
  active: string;
}

export function ScenePresetSwitcher({
  gtwinId,
  presets,
  active,
}: ScenePresetSwitcherProps) {
  const setActivePreset = useViewerStore((s) => s.setActivePreset);
  const liveRegion = useLiveRegion();
  const announcedPreset = useRef<string | null>(null);
  // Two-phase switch: pendingPreset is set immediately (shows spinner) then
  // the Zustand write is deferred by one rAF so the browser can paint the
  // loading state before starting the heavy Giro3D layer add/remove cascade.
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingPreset) return;
    const id = requestAnimationFrame(() => {
      setActivePreset(pendingPreset);
      setPendingPreset(null);
    });
    return () => cancelAnimationFrame(id);
  }, [pendingPreset, setActivePreset]);

  const resolvedPresets: ScenePreset[] =
    presets && presets.length > 0 ? presets : [...DEFAULT_PRESETS];

  function activatePreset(preset: ScenePreset) {
    if (preset.id === active || pendingPreset === preset.id) return;
    // Phase 1: show spinner immediately (this frame).
    // Phase 2: the useEffect fires setActivePreset on the next rAF so the
    // browser paints the loading indicator before the heavy work starts.
    setPendingPreset(preset.id);

    // Update URL hash without navigation (per dispatch §4)
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#preset=${preset.id}`);
    }

    // Persist to server (fire-and-forget; non-blocking per D3)
    void gtwinClient.scene.presets.activate(gtwinId, preset.id).catch(() => {
      // Non-blocking — log only; do not crash the UI
    });

    // Live region announcement (a11y binding)
    if (announcedPreset.current !== preset.id) {
      announcedPreset.current = preset.id;
      liveRegion.announce('status', `Scene preset: ${preset.label}`);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, preset: ScenePreset) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activatePreset(preset);
    }
  }

  return (
    <>
      {resolvedPresets.map((preset) => {
        const isActive = preset.id === active;
        const isSwitching = pendingPreset === preset.id;
        return (
          <button
            key={preset.id}
            type="button"
            role="button"
            aria-pressed={isActive}
            aria-busy={isSwitching}
            tabIndex={0}
            onClick={() => activatePreset(preset)}
            onKeyDown={(e) => handleKeyDown(e, preset)}
            data-testid={`preset-pill-${preset.id}`}
            data-preset-id={preset.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: tokens.space[1],
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              borderRadius: tokens.radius.pill,
              border: `${tokens.border.width.thin}px solid ${
                isActive
                  ? tokens.color.accent.primary
                  : tokens.color.ink.muted
              }`,
              background: isActive
                ? tokens.color.accent.primary
                : 'transparent',
              opacity: isSwitching ? 0.6 : 1,
              color: isActive
                ? tokens.color.accent.on
                : tokens.color.ink.secondary,
              fontSize: tokens.font.size.sm,
              fontWeight: isActive
                ? tokens.font.weight.semibold
                : tokens.font.weight.regular,
              fontFamily: tokens.font.family.ui,
              cursor: 'pointer',
              outline: 'none',
              transition: `background ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard},
                           color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard},
                           border-color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
            }}
            onFocus={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow =
                `0 0 0 ${tokens.focus.ringWidth}px ${tokens.focus.ringColor}`;
            }}
            onBlur={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            {preset.label}
          </button>
        );
      })}
    </>
  );
}
